import { Suspense, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { invariant } from "@apollo/client/utilities/globals";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Loader } from "lucide-react";
import { z } from "zod";

import { Section, SectionHeader } from "@roqua/components/sections";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@roqua/components/ui/accordion";

import { PageTitle } from "@/components/PageTitle";
import { ChooseNotificationTemplate } from "@/features/Notifications/ChooseNotificationTemplate";
import { ChooseRespondent } from "@/features/Notifications/ChooseRespondent";
import { NotificationTemplatesQuery } from "@/features/Notifications/graphql";
import { PreviewNotificationTemplate } from "@/features/Notifications/PreviewNotificationTemplate";
import { useTranslation } from "@/i18n/client";
import { respondentNameOrLabel } from "@/models/respondent";
import { useSessionContext } from "@/session";

const searchSchema = z.object({
  respondentId: z.coerce.string().nullable().default(null),
  notificationTemplateId: z.coerce.string().nullable().default(null),
});

export const Route = createFileRoute("/$organizationKey/$dossierId/notifications/new")({
  validateSearch: zodValidator(searchSchema),
  component: Page,
});

function Page() {
  const { organizationKey, dossierId } = useSessionContext();
  const { t } = useTranslation("Notifications");
  const { data } = useQuery(NotificationTemplatesQuery, {
    variables: { organizationKey, dossierId },
  });
  const searchParams = Route.useSearch();

  const [respondentId, setRespondentId] = useState<string | null>(searchParams.respondentId);
  const [notificationTemplateId, setNotificationTemplateId] = useState<string | null>(
    searchParams.notificationTemplateId,
  );
  const [step, setStep] = useState(initialStep(respondentId, notificationTemplateId));

  const respondent = (() => {
    if (!data) return null;
    if (!respondentId) return null;
    const r = data.dossier.respondents.find((r) => r.id === respondentId);
    invariant(r, "Respondent not found");
    return r;
  })();

  const notificationTemplate = (() => {
    if (!notificationTemplateId) return null;
    if (!data) return null;

    const nt = data.organization.notificationTemplates.find(
      (nt) => nt.id === notificationTemplateId,
    );
    invariant(nt, "Notification template not found");
    return nt;
  })();

  const notificationTemplates = useMemo(() => {
    if (!respondent) return [];
    if (!data) return [];

    return data.organization.notificationTemplates.filter((nt) =>
      nt.respondentTypes.includes(respondent.type),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, respondentId]);

  const chooseRespondent = (id: string) => {
    setRespondentId(id);
    setNotificationTemplateId(null);
    setStep("template-step");
  };

  const chooseNotificationTemplate = (id: string) => {
    setNotificationTemplateId(id);
    setStep("preview");
  };

  if (!data) return null;

  return (
    <>
      <PageTitle title="notificationsNew" />
      <Section variant="header">
        <SectionHeader header={t("createForm.header")} />
      </Section>
      <Section>
        <Accordion type="single" value={step} onValueChange={(v) => setStep(v)}>
          <AccordionItem value="respondent-step">
            <AccordionTrigger>
              {respondent === null
                ? t("createForm.chooseRespondent")
                : t("createForm.chosenRespondent", {
                    respondentName: respondentNameOrLabel(respondent),
                    respondentType: respondent.type,
                    responsesCount: respondent.responses.totalCount,
                  })}
            </AccordionTrigger>
            <AccordionContent>
              <ChooseRespondent
                respondents={data?.dossier.respondents}
                onChange={chooseRespondent}
              />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="template-step" disabled={!respondentId}>
            <AccordionTrigger>
              {notificationTemplate === null
                ? respondent === null
                  ? t("createForm.chooseNotificationTemplateAfterRespondent")
                  : t("createForm.chooseNotificationTemplate")
                : t("createForm.chosenNotificationTemplate", {
                    notificationTemplateName: notificationTemplate.name,
                  })}
            </AccordionTrigger>
            <AccordionContent>
              {respondent && (
                <Suspense fallback={<Loader />}>
                  <ChooseNotificationTemplate
                    notificationTemplates={notificationTemplates}
                    onChange={chooseNotificationTemplate}
                  />
                </Suspense>
              )}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="preview" disabled>
            <AccordionTrigger>
              {respondent === null || notificationTemplate === null
                ? t("createForm.previewAfterTemplate")
                : t("createForm.preview")}
            </AccordionTrigger>
            <AccordionContent>
              {respondent && notificationTemplate && (
                <PreviewNotificationTemplate
                  respondent={respondent}
                  notificationTemplate={notificationTemplate}
                />
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Section>
    </>
  );
}

function initialStep(respondentId: string | null, notificationTemplateId: string | null) {
  if (respondentId === null) return "respondent-step";
  if (notificationTemplateId === null) return "template-step";
  return "preview";
}
